import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ContactInput, OrganisationCreate } from '@hospasense/contracts';
import {
  CONSENT_BASES,
  CONTACT_PURPOSES,
  DEPLOYMENT_TYPES,
  ORGANISATION_TYPES,
} from '@hospasense/contracts';
import { ApiError } from '../lib/api.js';
import {
  useCreateOrganisation,
  useOperators,
  useOrganisation,
  useUpdateOrganisation,
} from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  PermissionDeniedState,
  Select,
  useToast,
} from '../components/ui/index.js';

interface FormState {
  legalName: string;
  displayName: string;
  organisationType: (typeof ORGANISATION_TYPES)[number];
  deploymentType: (typeof DEPLOYMENT_TYPES)[number];
  jurisdiction: string;
  tags: string;
  ownerUserIds: string[];
  contacts: ContactInput[];
}

const EMPTY: FormState = {
  legalName: '',
  displayName: '',
  organisationType: 'hospital',
  deploymentType: 'cloud',
  jurisdiction: 'IN',
  tags: '',
  ownerUserIds: [],
  contacts: [],
};

/** Create and edit share one form; edit carries the concurrency version. */
export function OrgFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const existing = useOrganisation(mode === 'edit' ? id : undefined);
  const operators = useOperators(hasPermission('organisation.owners.manage'));
  const createMutation = useCreateOrganisation();
  const updateMutation = useUpdateOrganisation(id ?? '');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && existing.data) {
      const o = existing.data;
      setForm({
        legalName: o.legalName,
        displayName: o.displayName,
        organisationType: o.organisationType,
        deploymentType: o.deploymentType,
        jurisdiction: o.jurisdiction,
        tags: o.tags.join(', '),
        ownerUserIds: o.owners.map((w) => w.userId),
        contacts: o.contacts.map((c) => ({
          fullName: c.fullName,
          roleTitle: c.roleTitle,
          email: c.email,
          ...(c.phone ? { phone: c.phone } : {}),
          isPrimary: c.isPrimary,
          purpose: c.purpose,
          consentBasis: c.consentBasis,
          ...(c.consentNote ? { consentNote: c.consentNote } : {}),
        })),
      });
    }
  }, [mode, existing.data]);

  const needed = mode === 'create' ? 'organisation.create' : 'organisation.update';
  if (!hasPermission(needed)) return <PermissionDeniedState what="organisation editing" />;
  if (mode === 'edit' && existing.isLoading) return <LoadingState />;
  if (mode === 'edit' && (existing.isError || !existing.data)) {
    return (
      <ErrorState
        description="This organisation could not be loaded."
        onRetry={() => void existing.refetch()}
      />
    );
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const payload: OrganisationCreate = {
      legalName: form.legalName.trim(),
      displayName: form.displayName.trim(),
      organisationType: form.organisationType,
      deploymentType: form.deploymentType,
      jurisdiction: form.jurisdiction.trim().toUpperCase(),
      tags: form.tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
      ownerUserIds: form.ownerUserIds,
      contacts: form.contacts,
    };
    try {
      if (mode === 'create') {
        const created = await createMutation.mutateAsync(payload);
        toast.success('Organisation created');
        navigate(`/organisations/${created.id}`);
      } else {
        await updateMutation.mutateAsync({ ...payload, expectedVersion: existing.data!.version });
        toast.success('Organisation updated');
        navigate(`/organisations/${id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
        setError(
          'Someone else changed this organisation while you were editing. Reload to get their changes, then re-apply yours.',
        );
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('The change could not be saved.');
      }
      toast.error('Save failed');
    }
  };

  const busy = createMutation.isPending || updateMutation.isPending;
  const title = mode === 'create' ? 'New organisation' : `Edit ${existing.data?.displayName ?? ''}`;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title={title}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Control', to: '/' },
              { label: 'Organisations', to: '/organisations' },
              { label: mode === 'create' ? 'New' : 'Edit' },
            ]}
          />
        }
      />
      <form onSubmit={(e) => void submit(e)} className="space-y-6" aria-label={title}>
        <Card>
          <CardHeader title="Identity" subtitle="Business identity only — never clinical data" />
          <CardBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Legal name"
                required
                minLength={2}
                maxLength={200}
                value={form.legalName}
                onChange={(e) => set('legalName', e.target.value)}
              />
              <Input
                label="Display name"
                required
                minLength={2}
                maxLength={200}
                value={form.displayName}
                onChange={(e) => set('displayName', e.target.value)}
              />
              <Select
                label="Organisation type"
                required
                value={form.organisationType}
                onChange={(e) =>
                  set('organisationType', e.target.value as FormState['organisationType'])
                }
              >
                {ORGANISATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {titleCase(t)}
                  </option>
                ))}
              </Select>
              <Select
                label="Deployment type"
                required
                value={form.deploymentType}
                onChange={(e) =>
                  set('deploymentType', e.target.value as FormState['deploymentType'])
                }
              >
                {DEPLOYMENT_TYPES.map((d) => (
                  <option key={d} value={d}>
                    {d === 'on_prem' ? 'On-prem' : 'Cloud'}
                  </option>
                ))}
              </Select>
              <Input
                label="Jurisdiction"
                required
                pattern="[A-Za-z]{2}(-[A-Za-z0-9]{1,3})?"
                hint="ISO 3166, e.g. IN or IN-KA"
                value={form.jurisdiction}
                onChange={(e) => set('jurisdiction', e.target.value)}
              />
              <Input
                label="Tags"
                hint="Comma-separated, lowercase (e.g. priority, south-region)"
                value={form.tags}
                onChange={(e) => set('tags', e.target.value)}
              />
            </div>
            {hasPermission('organisation.owners.manage') && (
              <fieldset>
                <legend className="mb-1 block text-xs font-medium text-text-secondary">
                  Internal owners
                </legend>
                <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
                  {(operators.data?.items ?? []).map((op) => (
                    <label key={op.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.ownerUserIds.includes(op.id)}
                        onChange={(e) =>
                          set(
                            'ownerUserIds',
                            e.target.checked
                              ? [...form.ownerUserIds, op.id]
                              : form.ownerUserIds.filter((x) => x !== op.id),
                          )
                        }
                        className="size-4 accent-(--primary)"
                      />
                      {op.displayName}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Contacts"
            subtitle="Business contacts only; at most one primary"
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  set('contacts', [
                    ...form.contacts,
                    {
                      fullName: '',
                      roleTitle: '',
                      email: '',
                      isPrimary: form.contacts.length === 0,
                      purpose: 'commercial' as const,
                      consentBasis: 'contract' as const,
                    },
                  ])
                }
              >
                Add contact
              </Button>
            }
          />
          <CardBody className="space-y-4">
            {form.contacts.length === 0 && (
              <p className="text-sm text-text-muted">
                No contacts yet. Organisations without contacts appear in “Needs attention”.
              </p>
            )}
            {form.contacts.map((contact, i) => (
              <div
                key={i}
                className="grid gap-3 rounded-lg border border-border p-3 md:grid-cols-2"
              >
                <Input
                  label="Full name"
                  required
                  value={contact.fullName}
                  onChange={(e) => updateContact(i, { fullName: e.target.value })}
                />
                <Input
                  label="Role title"
                  required
                  value={contact.roleTitle}
                  onChange={(e) => updateContact(i, { roleTitle: e.target.value })}
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  value={contact.email}
                  onChange={(e) => updateContact(i, { email: e.target.value })}
                />
                <Input
                  label="Phone"
                  value={contact.phone ?? ''}
                  onChange={(e) => updateContact(i, { phone: e.target.value })}
                  hint="Optional"
                />
                <Select
                  label="Purpose"
                  value={contact.purpose}
                  onChange={(e) =>
                    updateContact(i, { purpose: e.target.value as ContactInput['purpose'] })
                  }
                  hint="Why this person is stored"
                >
                  {CONTACT_PURPOSES.map((purpose) => (
                    <option key={purpose} value={purpose}>
                      {titleCase(purpose)}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Consent basis"
                  value={contact.consentBasis}
                  onChange={(e) =>
                    updateContact(i, {
                      consentBasis: e.target.value as ContactInput['consentBasis'],
                    })
                  }
                >
                  {CONSENT_BASES.map((basis) => (
                    <option key={basis} value={basis}>
                      {titleCase(basis)}
                    </option>
                  ))}
                </Select>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="primary-contact"
                    checked={contact.isPrimary}
                    onChange={() =>
                      set(
                        'contacts',
                        form.contacts.map((c, j) => ({ ...c, isPrimary: j === i })),
                      )
                    }
                    className="size-4 accent-(--primary)"
                  />
                  Primary contact
                </label>
                <div className="flex items-end justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      set(
                        'contacts',
                        form.contacts.filter((_, j) => j !== i),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>

        {error && (
          <p role="alert" className="rounded-lg bg-error-bg-subtle px-3 py-2 text-sm text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            {mode === 'create' ? 'Create organisation' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );

  function updateContact(index: number, patch: Partial<ContactInput>): void {
    set(
      'contacts',
      form.contacts.map((c, j) => {
        if (j !== index) return c;
        const merged = { ...c, ...patch };
        if (merged.phone === '') delete merged.phone;
        return merged;
      }),
    );
  }
}
